import { domainOf } from './access';

export const WEEKLY_UPLOAD_LIMIT = 5;
export const WEEKLY_ANALYSIS_LIMIT = 5;

/**
 * What a 240 account gets instead.
 *
 * Both numbers move together on purpose. An analysis consumes the upload it reads — the
 * source object is deleted the moment processing begins, and re-reading the same plan means
 * uploading it again — so a raised analysis ceiling behind the teacher upload ceiling would
 * buy nothing at all. Raising one without the other is not a smaller version of this change;
 * it is a change with no effect.
 */
export const STAFF_UPLOAD_LIMIT = 50;
export const STAFF_ANALYSIS_LIMIT = 50;

/**
 * Who gets the raised ceilings. Deliberately NOT `ALLOWED_EMAIL_DOMAINS` from `core/access`,
 * which also carries `240certification.com`: access and spend are different questions and
 * they are allowed to have different answers. Matching is on the whole domain, via the same
 * `domainOf` the access rule uses, so `a@240tutoring.com.evil.test` is a teacher here too.
 */
const STAFF_QUOTA_DOMAINS = ['240tutoring.com'] as const;

/** A rolling week; deleting reports never restores usage. */
export const QUOTA_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export type QuotaKind = 'uploads' | 'analyses';

export type QuotaLimits = Readonly<Record<QuotaKind, number>>;

export type QuotaDecision =
  | { ok: true; remaining: number }
  | { ok: false; reason: 'quota_exceeded'; kind: QuotaKind };

export interface QuotaInput {
  kind: QuotaKind;
  /** Rows the caller already has inside the window. */
  used: number;
  limit?: number;
}

export const TEACHER_LIMITS: QuotaLimits = {
  uploads: WEEKLY_UPLOAD_LIMIT,
  analyses: WEEKLY_ANALYSIS_LIMIT,
};

export const STAFF_LIMITS: QuotaLimits = {
  uploads: STAFF_UPLOAD_LIMIT,
  analyses: STAFF_ANALYSIS_LIMIT,
};

/**
 * The ceilings that apply to one address. Pure, and the single place the two sets are chosen
 * between — every caller that enforces, reserves or reports a limit reads it from here, so
 * the number the page shows and the number the SQL binds cannot drift apart.
 *
 * An absent address is a real case (it depends on the Clerk JWT template) and it gets the
 * teacher ceilings. The failure direction matters: an unreadable claim costs a 240 account
 * some attempts, it does not hand a stranger fifty.
 */
export function limitsFor(
  email?: string | null,
  domains: readonly string[] = STAFF_QUOTA_DOMAINS,
): QuotaLimits {
  const domain = domainOf((email ?? '').trim().toLowerCase());
  return domain !== null && domains.includes(domain) ? STAFF_LIMITS : TEACHER_LIMITS;
}

const LIMIT_OF: QuotaLimits = TEACHER_LIMITS;

export function decideQuota({ kind, used, limit = LIMIT_OF[kind] }: QuotaInput): QuotaDecision {
  if (used >= limit) return { ok: false, reason: 'quota_exceeded', kind };
  return { ok: true, remaining: limit - used };
}

/** The ISO instant one window before `nowIso`, for the `created_at >= ?` bind. */
export function quotaWindowStart(nowIso: string): string {
  return new Date(new Date(nowIso).getTime() - QUOTA_WINDOW_MS).toISOString();
}
